import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { HeaderLogo } from '@/shared/components/custom/layout/header/HeaderLogo';
import { OnboardingProgress } from './OnboardingProgress';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import type { AuthUser } from '@/core/types/user.types';
import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';

interface OnboardingLayoutProps {
  children: React.ReactNode;
  user: AuthUser | null;
  progress: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  canGoNext: boolean;
  isPending: boolean;
  optional?: boolean;
  onBack: () => void;
  onNext: () => void;
  onSkip?: () => void;
}

export function OnboardingLayout({
  children,
  user,
  progress,
  isFirstStep,
  isLastStep,
  canGoNext,
  isPending,
  // optional,
  onBack,
  onNext,
  // onSkip,
}: OnboardingLayoutProps) {
  const { t } = useTranslation('onboarding');
  return (
    <Dialog open modal>
      <DialogContent
        // Deshabilita el cierre con Escape y click fuera
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        // Oculta el botón X de cierre
        showCloseButton={false}
        className="flex flex-col gap-0 p-0 sm:max-w-6xl min-h-[60vh] max-h-[90vh] overflow-hidden bg-[radial-gradient(140%_120%_at_50%_80%,transparent_50%,#299FF9_100%)]"
      >
        {/* Header del modal */}
        <DialogHeader className="flex flex-row items-center justify-between px-8 py-4 ">
          <DialogTitle className="sr-only">{t('title')}</DialogTitle>
          <HeaderLogo size="sm" />
          {user && (
            <span className="text-sm text-primary font-medium">
              {t('welcome', { name: user.name })}
            </span>
          )}
        </DialogHeader>

        {/* Contenido del paso */}
        <div className="flex flex-col  justify-center flex-1 overflow-y-auto w-full px-28 py-10">
          {children}
        </div>

        {/* Footer con navegación y progress */}
        <div className="px-8 py-5">
          <div className="flex items-center gap-6">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={isPending}
              className={cn(
                'flex items-center gap-2 text-muted-foreground',
                isFirstStep && 'invisible',
              )}
              data-testid="onboarding-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('navigation.back')}
            </Button>

            <div className="flex-1">
              <OnboardingProgress progress={progress} />
            </div>

            {/* {optional && onSkip && (
              <Button
                type="button"
                variant="outline"
                onClick={onSkip}
                disabled={isPending}
                className="text-muted-foreground"
              >
                Saltar
              </Button>
            )} */}

            <Button
              type="button"
              variant="ghost"
              onClick={onNext}
              disabled={!canGoNext || isPending}
              className="flex items-center gap-2"
              data-testid="onboarding-next-btn"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('navigation.saving')}
                </>
              ) : (
                <>
                  {isLastStep ? t('navigation.finish') : t('navigation.next')}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
